import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Api from "services/api";
import toast from "react-hot-toast";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { userSelector } from "slices/userSlice";
import { usePermissions } from "layouts/Admin";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import ConfirmationStatus from "components/common/ConfirmationMessageStatus";
import { Button } from "@mui/material";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { getBackgroundStyle } from "components/common/TableColor";
import ReportsFilter from "components/common/Filter";

// Redux slice (you will create or use existing appointmentSlice)
import { fetchAllAppointments, downloadFullReport } from "slices/appointmentSlice";

const apiService = new Api();

function Reports() {
    const dispatch = useDispatch();
    const { checkPermission } = usePermissions();
    const { user } = useSelector(userSelector);
    const userToken = user.token;

    const [appointments, setAppointments] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [loader, setLoader] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLength, setPageLength] = useState(10);
    const [total, setTotal] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({
        sortBy: "date",
        sortDirection: "asc",
    });
    const [filterDates, setFilterDates] = useState({
        start_date: "",
        end_date: "",
    });

    // Filter/Search logic
    const handleFilter = (data) => {
        console.log(data, 'data')
        setLoader(true);
        dispatch(fetchAllAppointments(1, data, false, false, (res) => {
            console.log(res, 'res')
            setAppointments(res.data);
            setTotalPages(res.last_page);
            setCurrentPage(res.current_page);
            setPageLength(res.per_page);
            setTotal(res.total);
            setLoader(false);
        }));
    };

    const handleReset = (defaultValues) => {
        setLoader(true);
        dispatch(fetchAllAppointments(1, defaultValues, (res) => {
            setAppointments(res.data);
            setTotalPages(res.last_page);
            setCurrentPage(res.current_page);
            setPageLength(res.per_page);
            setTotal(res.total);
            setLoader(false);
        }));
    };

    const renderTableHeader = React.useCallback(() => {
        const callBack = (dir, sortBy) => {
            setSortConfig({ sortBy, sortDirection: dir });
            dispatch(fetchAllAppointments("", "", (res) => {
                setAppointments(res.data);
                setTotalPages(res.last_page);
                setCurrentPage(res.current_page);
                setPageLength(res.per_page);
                setTotal(res.total);
                setLoader(false);
            }));
        };

        return (
            <tr>
                <th>#</th>
                <th>
                    <TableHeaderItem
                        title="Appointment Id"
                        sortBy="appt_no"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th>
                    <TableHeaderItem
                        title="Type"
                        sortBy="interpreter"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th>
                    <TableHeaderItem
                        title="Checkin Datetime"
                        sortBy="client"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th>
                    <TableHeaderItem
                        title="Checkout Datetime"
                        sortBy="date"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
                <th>
                    <TableHeaderItem
                        title="Mileage"
                        sortBy="start_time"
                        sortDirection={sortConfig.sortDirection}
                        callBack={callBack}
                    />
                </th>
            </tr>
        );
    }, [sortConfig, currentPage]);

    const renderTableContent = React.useCallback(() => {
        console.log("Reports Debug → currentPage:", currentPage, "pageLength:", pageLength);
        return (
            <>
                {appointments?.map((item, index) => (
                    <AppointmentItem
                        key={index}
                        data={item}
                        index={index}
                        currentPage={currentPage}
                        pageLength={pageLength}
                    />
                ))}
            </>
        );
    }, [appointments]);

    const callViewApi = async () => {
        handleFilter(filterDates);

    };


    return (
        <>
            <ReportsFilter
                onFilter={handleFilter}
                onReset={handleReset}
                setFilterDates={setFilterDates}
                setLoader={setLoader}
                showDownloadButton={true}
            />
            <ListLayout
                time={false}
                addButtonDisabled={true}
                renderTableHeader={renderTableHeader}
                renderTableContent={renderTableContent}
                hideRemoveButton={true}
                totalPages={totalPages}
                fetchList={() => {
                    dispatch(fetchAllAppointments("", "", "", "", (res) => {
                        setAppointments(res.data);
                        setTotalPages(res.last_page);
                        // setCurrentPage(res.current_page);
                        setPageLength(res.per_page);
                        setTotal(res.total);
                        setLoader(false);
                    }));
                }}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                loader={loader}
                noOfItems={total}
                containerStyle={{ marginTop: "0" }}
                searchPlaceholder="Search completed appointments..."
            />
        </>
    );
}

const AppointmentItem = ({ data, index, currentPage, pageLength }) => {
    console.log("AppointmentItem props:", { index, currentPage, pageLength });
    const serial = useMemo(() => {
        return index + 1 + (currentPage - 1) * pageLength;
    }, [currentPage, pageLength]);

    console.log(data, 'data')
    return (
        <tr>
            <td>{serial}</td>
            <td>
                <Link to={`/appointments/view/${data?.id}`} className="link_preview">
                    {data?.appid || "N/A"}
                </Link>
            </td>
            <td>
                {{
                    0: "Interpreting Request",
                    1: "After Hours",
                    2: "Bilingual",
                }[data?.type] || "N/A"}
            </td>
            <td>{data?.appointment_assign?.checkin_date && data?.appointment_assign?.checkin_time ? `${data.appointment_assign.checkin_date} ${moment(data.appointment_assign.checkin_time, "HH:mm:ss").format("HH:mm")}` : ""}
            </td>
            <td>{data?.appointment_assign?.checkout_date && data?.appointment_assign?.checkout_time ? `${data.appointment_assign.checkout_date} ${moment(data.appointment_assign.checkout_time, "HH:mm:ss").format("HH:mm")}` : ""}
            </td>

            <td>{data?.appointment_assign?.mileage || "N/A"}</td>
        </tr>
    );
};

export default Reports;

// ---------------- FILTER COMPONENT ----------------

// function downloadBlobFile(blob, filename = "report.xlsx") {
//   const url = window.URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.href = url;
//   a.download = filename;
//   document.body.appendChild(a);
//   a.click();
//   a.remove();
//   window.URL.revokeObjectURL(url);
// }

// const ReportsFilter = ({ onFilter, onReset, setFilterDates, setLoader }) => {
//     const dispatch = useDispatch();
//     const defaultValues = {
//         start_date: "",
//         end_date: "",
//         interpreter_id: "",
//     };
//     const { handleSubmit, control, reset, getValues } = useForm({
//         defaultValues: defaultValues,
//     });

//     const handleResetFields = () => {
//         reset(defaultValues);
//         onReset(defaultValues);
//     };

//     const onSubmit = (data) => {
//         onFilter(data);
//         setFilterDates(data);
//     };

//      const handleDownloadReport = () => {
//         setLoader(true)
//     const filters = getValues(); // current filter values
//     console.log(filters , 'filters')
//     // dispatch the thunk. We'll implement the thunk next — it should call the backend and return a file blob / data-URI / url via the callback.
//     dispatch(
//       downloadFullReport(filters, (success, res) => {
//         if (!success) {
//         setLoader(false)
//           Swal.fire({
//             title: "Error!",
//             text: (res && res.message) || "Failed to download report",
//             icon: "error",
//             confirmButtonColor: "#d33",
//           });
//           return;
//         }
//         setLoader(false)
//         // res could be:
//         // 1) a Blob
//         // 2) { blob, filename }
//         // 3) a data URI string (data:...)
//         // 4) a URL string
//         try {
//           // 1) Blob or {blob, filename}
//           if (res instanceof Blob) {
//             downloadBlobFile(res);
//             Swal.fire({ title: "Success!", text: "Report downloaded", icon: "success", confirmButtonColor: "#3085d6" });
//             return;
//           }

//           if (res && res.blob) {
//             downloadBlobFile(res.blob, res.filename || "appointments_report.xlsx");
//             Swal.fire({ title: "Success!", text: "Report downloaded", icon: "success", confirmButtonColor: "#3085d6" });
//             return;
//           }

//           // 2) data URI
//           if (typeof res === "string" && res.startsWith("data:")) {
//             const arr = res.split(",");
//             const mimeMatch = arr[0].match(/:(.*?);/);
//             const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
//             const bstr = atob(arr[1]);
//             let n = bstr.length;
//             const u8 = new Uint8Array(n);
//             while (n--) u8[n] = bstr.charCodeAt(n);
//             const blob = new Blob([u8], { type: mime });
//             downloadBlobFile(blob);
//             Swal.fire({ title: "Success!", text: "Report downloaded", icon: "success", confirmButtonColor: "#3085d6" });
//             return;
//           }

//           // 3) URL string (pointing to a file)
//           if (typeof res === "string") {
//             const a = document.createElement("a");
//             a.href = res;
//             a.download = ""; // let server suggest name or browser will handle
//             a.target = "_blank";
//             document.body.appendChild(a);
//             a.click();
//             a.remove();
//             Swal.fire({ title: "Success!", text: "Report download started", icon: "success", confirmButtonColor: "#3085d6" });
//             return;
//           }

//           // fallback
//           Swal.fire({ title: "Error!", text: "Unexpected response format from server", icon: "error", confirmButtonColor: "#d33" });
//         } catch (err) {
//           console.error("Download error:", err);
//           Swal.fire({ title: "Error!", text: "Could not process downloaded file", icon: "error", confirmButtonColor: "#d33" });
//         }
//       })
//     );
//   };


//     return (
//         <div className="content" style={{ minHeight: "auto" }}>
//             <div className="row_main_head">
//                 <h2 className="main_heading">Reports</h2>
//             </div>
//             <div className="card_table">
//                 <form className="invoicing_form" onSubmit={handleSubmit(onSubmit)}>
//                     <div className="reports row_Invoice">
//                         <div className="row-primary">
//                             <h2>From Date</h2>
//                             <Controller
//                                 name="start_date"
//                                 control={control}
//                                 render={({ field }) => (
//                                     <div className="input-image">
//                                         <input
//                                             type="date"
//                                             required
//                                             {...field}
//                                             value={field.value}
//                                             onChange={(e) =>
//                                                 field.onChange(moment(e.target.value).format("YYYY-MM-DD"))
//                                             }
//                                         />
//                                         <img src="/images/celender.png" alt="calendar Icon" className="custom-icon" />
//                                     </div>
//                                 )}
//                             />
//                         </div>
//                         <div className="row-primary">
//                             <h2>To Date</h2>
//                             <Controller
//                                 name="end_date"
//                                 control={control}
//                                 render={({ field }) => (
//                                     <div className="input-image">
//                                         <input
//                                             type="date"
//                                             required
//                                             {...field}
//                                             value={field.value}
//                                             onChange={(e) =>
//                                                 field.onChange(moment(e.target.value).format("YYYY-MM-DD"))
//                                             }
//                                         />
//                                         <img src="/images/celender.png" alt="calendar Icon" className="custom-icon" />
//                                     </div>
//                                 )}
//                             />
//                         </div>
//                     </div>

//                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
//                         <div className="row-primary btns_area row_10 mb-0">
//                             <SubmitButton btnText="Search" buttonType="submit" className={"green"} />
//                             <SubmitButton buttonType="" className={"lightBlue"} btnText="Reset" onClick={handleResetFields} />
//                         </div>
//                         <button
//                             type="button"
//                             style={{ height: "fit-content" }}
//                             className="main_button"
//                         onClick={() => handleDownloadReport()}
//                         >
//                             Download Full Report
//                         </button>
//                     </div>
//                 </form>
//             </div>
//         </div>
//     );
// };
